import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { ArrowLeft, Pencil, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { RhfStateCitySelect } from '@/components/common/RhfStateCitySelect';
import {
  Avatar,
  Badge,
  Button,
  Card,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SelectField,
  Spinner,
  useToast,
} from '@/components/ui';
import { buildUserEditPayload, userEditSchema } from '@/features/users/userEditSchema';
import { useDesignations } from '@/hooks/useCompanySettings';
import { useUser, useUpdateUser } from '@/hooks/useUsers';
import { optionsWithStored } from '@/lib/designations';
import { ApprovalStatus } from '@/types';
import { ROUTES } from '@/utils/constants';
import { formatDate, formatDateTime, todayIso } from '@/utils/format';

const statusVariant: Record<ApprovalStatus, 'success' | 'warning' | 'danger'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

// Whole rupees, capped to match the server validator (max 1,000,000,000).
const targetField = z
  .string()
  .trim()
  .regex(/^\d{1,10}$/, 'Digits only')
  .refine((v) => Number(v) <= 1_000_000_000, 'Cannot exceed 100,00,00,000')
  .optional()
  .or(z.literal(''));

// Shared employee fields plus the admin-only sales targets.
const editSchema = userEditSchema.extend({
  monthlyTarget: targetField,
  yearlyTarget: targetField,
});

type EditValues = z.infer<typeof editSchema>;

const AdminUserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(false);
  const { data: user, isLoading, isError } = useUser(userId);
  const updateUser = useUpdateUser();

  // Same admin-configured vocabulary the onboarding form offers, so an
  // employee's department does not depend on which screen created them.
  const { data: designations, isLoading: designationsLoading } = useDesignations();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<EditValues>({ resolver: zodResolver(editSchema) });

  useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        email: user.email ?? '',
        dateOfBirth: user.dateOfBirth ?? '',
        dateOfJoining: user.dateOfJoining ?? '',
        designationLevel: user.designationLevel ?? '',
        designationDept: user.designationDept ?? '',
        address: user.address ?? '',
        pincode: user.pincode ?? '',
        city: user.city ?? '',
        state: user.state ?? '',
        employeeCode: user.employeeCode ?? '',
        monthlyTarget: user.monthlyTarget != null ? String(user.monthlyTarget) : '',
        yearlyTarget: user.yearlyTarget != null ? String(user.yearlyTarget) : '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (values: EditValues): Promise<void> => {
    if (!user) return;
    const payload: Parameters<typeof updateUser.mutateAsync>[0]['payload'] = {
      ...buildUserEditPayload(values),
      monthlyTarget: values.monthlyTarget ? Number(values.monthlyTarget) : null,
      yearlyTarget: values.yearlyTarget ? Number(values.yearlyTarget) : null,
    };
    try {
      await updateUser.mutateAsync({ id: user.id, payload });
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      const message =
        err instanceof AxiosError
          ? ((err.response?.data as { message?: string })?.message ?? 'Update failed')
          : 'Update failed';
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="py-12 text-center text-sm text-danger">
        Failed to load user. Please try again.
      </div>
    );
  }

  const onToggleStatus = async (): Promise<void> => {
    if (!user) return;
    try {
      await updateUser.mutateAsync({ id: user.id, payload: { isActive: !user.isActive } });
      toast.success(user.isActive ? 'Account deactivated' : 'Account activated');
      setStatusConfirm(false);
    } catch (err) {
      const message =
        err instanceof AxiosError
          ? ((err.response?.data as { message?: string })?.message ?? 'Could not update status')
          : 'Could not update status';
      toast.error(message);
      setStatusConfirm(false);
    }
  };

  const formatTarget = (value: number | null | undefined): string =>
    value != null ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';

  const field = (label: string, value: string | null | undefined) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink/40">{label}</span>
      <span className="text-sm font-medium text-ink">{value || '-'}</span>
    </div>
  );

  const metaField = (label: string, value: ReactNode) => (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink/40">{label}</span>
      {value}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            void navigate(ROUTES.ADMIN_USERS);
          }}
          className="flex items-center gap-1.5 text-sm font-semibold text-ink/50 transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" />
          All users
        </button>
      </div>

      <Card className="mt-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar name={user.name} size="lg" />
            <div className="min-w-0">
              <p className="truncate text-lg font-bold text-ink">{user.name}</p>
              <p className="truncate text-sm text-ink/60">+91 {user.mobile}</p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant={user.isActive ? 'danger' : 'secondary'}
            onClick={() => setStatusConfirm(true)}
          >
            {user.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </div>

        <div className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-2 lg:grid-cols-4">
          {metaField(
            'Role',
            <Badge
              variant={user.role === 'admin' ? 'info' : 'neutral'}
              className="w-fit capitalize"
            >
              {user.role}
            </Badge>,
          )}
          {metaField(
            'Job role',
            <span className="text-sm font-medium text-ink">{user.jobRole || '-'}</span>,
          )}
          {metaField(
            'Approval',
            <Badge variant={statusVariant[user.approvalStatus]} className="w-fit capitalize">
              {user.approvalStatus}
            </Badge>,
          )}
          {metaField(
            'Account status',
            <Badge variant={user.isActive ? 'success' : 'danger'} className="w-fit">
              {user.isActive ? 'Active' : 'Inactive'}
            </Badge>,
          )}
        </div>
      </Card>

      <Dialog
        open={statusConfirm}
        onOpenChange={(open) => {
          if (!open) setStatusConfirm(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{user.isActive ? 'Deactivate' : 'Activate'} this account?</DialogTitle>
            <DialogDescription>
              {user.isActive
                ? `${user.name} will immediately lose access to the CRM. All their sessions will be revoked.`
                : `${user.name} will regain access to the CRM.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setStatusConfirm(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={user.isActive ? 'danger' : 'primary'}
              isLoading={updateUser.isPending}
              onClick={() => {
                onToggleStatus().catch(() => {});
              }}
            >
              {user.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="mt-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold tracking-tight text-ink">Profile details</h2>
          {!editing ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false);
                reset();
              }}
              className="flex items-center gap-1.5"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
          )}
        </div>

        {editing ? (
          <form
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e).catch(() => {});
            }}
            noValidate
            className="flex flex-col gap-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Full name"
                type="text"
                error={errors.name?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('name')}
              />
              <Input
                label="Email address"
                type="email"
                error={errors.email?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('email')}
              />
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field: dateField }) => (
                  <DatePicker
                    label="Date of birth"
                    value={dateField.value}
                    onChange={dateField.onChange}
                    max={todayIso()}
                    error={errors.dateOfBirth?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="dateOfJoining"
                render={({ field: dateField }) => (
                  <DatePicker
                    label="Date of joining"
                    value={dateField.value}
                    onChange={dateField.onChange}
                    max={todayIso()}
                    error={errors.dateOfJoining?.message}
                  />
                )}
              />
              <Controller
                name="designationLevel"
                control={control}
                render={({ field: selected }) => (
                  <SelectField
                    label="Designation level"
                    disabled={designationsLoading}
                    error={errors.designationLevel?.message}
                    value={selected.value ?? ''}
                    onChange={(e) => selected.onChange(e.target.value)}
                  >
                    <option value="">Not set</option>
                    {/* The stored value is appended when the configured list no
                    longer offers it - see lib/designations. Without it the
                    select would silently show "Not set" and saving would clear
                    a department the employee really has. */}
                    {optionsWithStored(designations?.prefixes ?? [], user?.designationLevel).map(
                      (option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                    )}
                  </SelectField>
                )}
              />
              <Controller
                name="designationDept"
                control={control}
                render={({ field: selected }) => (
                  <SelectField
                    label="Department"
                    disabled={designationsLoading}
                    error={errors.designationDept?.message}
                    value={selected.value ?? ''}
                    onChange={(e) => selected.onChange(e.target.value)}
                  >
                    <option value="">Not set</option>
                    {optionsWithStored(designations?.roles ?? [], user?.designationDept).map(
                      (option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                    )}
                  </SelectField>
                )}
              />
              <Input
                label="Pincode"
                type="text"
                inputMode="numeric"
                error={errors.pincode?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('pincode')}
              />
              {/* Dropdowns, not free text (Fix 3) - see components/common/StateCitySelect. */}
              <RhfStateCitySelect
                control={control}
                setValue={setValue}
                stateName="state"
                cityName="city"
                labelClassName="text-sm font-medium text-foreground"
                stateError={errors.state?.message}
                cityError={errors.city?.message}
              />
              <Input
                label="Posting location address"
                type="text"
                className="sm:col-span-2"
                error={errors.address?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('address')}
              />
              <Input
                label="Employee code"
                type="text"
                maxLength={10}
                placeholder="e.g. SRK"
                className="sm:col-span-2"
                error={errors.employeeCode?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('employeeCode')}
              />
              <Input
                label="Monthly target (₹)"
                type="text"
                inputMode="numeric"
                placeholder="e.g. 1200000"
                error={errors.monthlyTarget?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('monthlyTarget')}
              />
              <Input
                label="Yearly target (₹)"
                type="text"
                inputMode="numeric"
                placeholder="e.g. 12000000"
                error={errors.yearlyTarget?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('yearlyTarget')}
              />
            </div>
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!isDirty}
              className="self-start"
            >
              Save changes
            </Button>
          </form>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {field('Full name', user.name)}
            {field('Email address', user.email)}
            {field('Date of birth', user.dateOfBirth)}
            {field('Date of joining', user.dateOfJoining)}
            {field('Designation level', user.designationLevel)}
            {field('Department', user.designationDept)}
            {field('Pincode', user.pincode)}
            {field('City', user.city)}
            {field('State', user.state)}
            <div className="sm:col-span-2">{field('Posting location address', user.address)}</div>
            <div className="sm:col-span-2">{field('Employee code', user.employeeCode)}</div>
            {field('Monthly target', formatTarget(user.monthlyTarget))}
            {field('Yearly target', formatTarget(user.yearlyTarget))}
          </div>
        )}
      </Card>

      <Card className="mt-4">
        <h2 className="mb-4 text-base font-bold tracking-tight text-ink">Account activity</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {field('Last login', user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never')}
          {field('Registered', formatDate(user.createdAt))}
          {field(
            'Onboarding completed',
            user.onboardingCompletedAt ? formatDate(user.onboardingCompletedAt) : 'Not yet',
          )}
        </div>
      </Card>
    </div>
  );
};

export default AdminUserDetailPage;
